/**
 * Cálculo ao vivo do HRN na tela de apreciação.
 *
 * A escala (valores e faixas) vem do servidor; aqui só se multiplica e se procura a faixa. O que
 * fica gravado é sempre o cálculo do servidor, que a tabela mostra depois de salvar.
 */
import type { HrnFactors, HrnScale, HrnScaleBand } from '../../../../types/machine';

export const HRN_FACTOR_KEYS: ReadonlyArray<keyof HrnFactors> = [
  'likelihood',
  'exposureFrequency',
  'harmSeverity',
  'peopleExposed',
];

/** Resposta de cada fator na tela: o valor escolhido, ou vazio enquanto não respondido. */
export type HrnAnswers = Record<keyof HrnFactors, number | ''>;

export const EMPTY_HRN_ANSWERS: HrnAnswers = {
  likelihood: '',
  exposureFrequency: '',
  harmSeverity: '',
  peopleExposed: '',
};

export interface HrnResultValue {
  hrn: number;
  level: string;
}

export type HrnTone = 'success' | 'warning' | 'error' | 'default';

/** Os quatro fatores respondidos, ou `null` se falta algum. */
export function completeFactors(answers: HrnAnswers): HrnFactors | null {
  const { likelihood, exposureFrequency, harmSeverity, peopleExposed } = answers;
  if (likelihood === '' || exposureFrequency === '' || harmSeverity === '' || peopleExposed === '') {
    return null;
  }
  return { likelihood, exposureFrequency, harmSeverity, peopleExposed };
}

export function answeredCount(answers: HrnAnswers): number {
  return HRN_FACTOR_KEYS.filter((key) => answers[key] !== '').length;
}

function bandFor(hrn: number, bands: HrnScaleBand[]): HrnScaleBand | undefined {
  return bands.find((band) => band.upTo !== null && hrn <= band.upTo) ?? bands.find((band) => band.upTo === null);
}

/** Multiplica os quatro fatores (duas casas, como o servidor) e acha a faixa da escala lida. */
export function calculateHrn(answers: HrnAnswers, scale: HrnScale): HrnResultValue | null {
  const factors = completeFactors(answers);
  if (factors === null) return null;
  const product = HRN_FACTOR_KEYS.reduce((acc, key) => acc * factors[key], 1);
  const hrn = Math.round(product * 100) / 100;
  const band = bandFor(hrn, scale.bands);
  return band ? { hrn, level: band.level } : null;
}

/** A cor vem da posição do nível na escala; o nível sempre aparece também em palavra. */
export function toneOfLevel(level: string | null, bands: HrnScaleBand[]): HrnTone {
  const position = bands.findIndex((band) => band.level === level);
  if (position < 0) return 'default';
  if (position < 3) return 'success';
  return position < 5 ? 'warning' : 'error';
}

/** "320" ou "2,4": vírgula decimal, sem zeros à toa. */
export const formatHrnNumber = (value: number): string => value.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
